import AdminHeader from "components/Headers/AdminHeader";
import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  Label,
  FormGroup,
  Input,
  Container,
  Row,
  Col,
  Collapse,
  Button,
} from "reactstrap";
import {
  REGEX,
  SELECT_CUSTOM_STYLES,
  MAX_DESCRIPTION_LENGTH,
} from "constants/CommonConstants";
import Select from "react-select";
import {
  batchSectionLabels,
  MODULES,
  COURSE_LANGUAGES,
  batchFieldToSectionMap,
} from "components/constant";
import ReactQuill from "react-quill";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import InfoPopover from "components/CommonComponents/InfoPopover";
import DatePicker from "react-datepicker";
import {
  addBatch,
  getBatchById,
  updateBatchById,
  getActiveCourseNameList,
} from "action/batch";
import { getTrainingCenterList } from "action/trainingCenter";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { getInstructorList } from "action/instructor";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { formatTime24Hour, parseTimeStringToDate } from "components/CommonComponents/CommonFunctions";
import CustomTimePicker from "components/CommonComponents/CustomTimePicker";

const initialFields = {
  courseID: {
    value: "",
    error: "",
    isChanged: false,
  },
  name: {
    value: "",
    error: "",
    isChanged: false,
  },
  registrationStartDate: {
    value: "",
    error: "",
    isChanged: false,
  },
  registrationEndDate: {
    value: "",
    error: "",
    isChanged: false,
  },
  batchStartDate: {
    value: "",
    error: "",
    isChanged: false,
  },
  batchEndDate: {
    value: "",
    error: "",
    isChanged: false,
  },
  batchStartTime: {
    value: "",
    error: "",
    isChanged: false,
  },
  batchEndTime: {
    value: "",
    error: "",
    isChanged: false,
  },
  trainerIds: {
    value: "",
    error: "",
    isChanged: false,
  },
  Trainers: {
    value: [], //multi -> array
    error: "",
    isChanged: false,
  },
  description: {
    value: "",
    error: "",
    isChanged: false,
  },
  trainingCenterID: {
    value: "",
    error: "",
    isChanged: false,
  },
  minParticipant: {
    value: "",
    error: "",
    isChanged: false,
  },
  maxParticipant: {
    value: "",
    error: "",
    isChanged: false,
  },
  virtualMeetingUrl: {
    value: "",
    error: "",
    isChanged: false,
  },
  capacityStatus: {
    value: "",
    error: "",
    isChanged: false,
  },
  batchLanguage: {
    value: "",
    error: "",
    isChanged: false,
  },
  status: {
    value: "ACTIVE",
    error: "",
    isChanged: false,
  },
  scheduleInfo: {
    value: "",
    error: "",
    isChanged: false,
  },
};

function AddBatch() {
  const { id } = useParams();
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const decodedId = id ? urlDecodedId(id) : null;
  const navigate = useNavigate();
  const descriptionQuillRef = useRef(null);
  const scheduleInfoQuillRef = useRef(null);
  const location = useLocation();
  // const mode = location.state?.mode || "add";
  const courseListID = location.state?.courseID || 0;
  const mode = id ? "edit" : "add";
  const [formData, setFormData] = useState(initialFields);
  const [open, setOpen] = useState({
    batchInfo: true,
    capacityEnrollment: false,
    registrationTimeline: false,
    batchSchedule: false,
    description: false,
    scheduleInfo: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  const toggle = (key) => {
    setOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const SectionHeader = ({ title, isOpen, onClick }) => (
    <div className="d-flex align-items-center cursor" onClick={onClick}>
      <i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} />
      <h5 className="ml-2 mb-0 section-header-title">{title}</h5>
    </div>
  );

  const courseNameListData = useSelector(
    (state) => state.batch.activeCourseNameList,
  );
  const selectedCourse = courseNameListData?.data?.find(
    (course) => course.ID === formData.courseID.value,
  );
  const isOfflineCourse =
    selectedCourse?.courseType === "LIVE" &&
    selectedCourse?.contentDeliveryMode === "OFFLINE";

  const selectedCourseLanguage = selectedCourse?.courseLanguage || "";

  const trainingCentersDataList = useSelector(
    (state) => state.trainingCenters.trainingCenterList,
  );

  const instructorListData = useSelector(
    (state) => state.instructor.instructorList,
  );

  useEffect(() => {
    if (!courseListID || !courseNameListData?.data?.length) {
      return;
    }

    const course = courseNameListData.data.find(
      (item) => Number(item.ID) === Number(courseListID),
    );

    if (!course) {
      return;
    }

    handleChange("courseID", Number(course.ID));

    if (course.courseLanguage === "ENGLISH") {
      handleChange("batchLanguage", "ENGLISH");
    } else if (course.courseLanguage === "HINDI") {
      handleChange("batchLanguage", "HINDI");
    } else {
      handleChange("batchLanguage", "");
    }
  }, [courseListID, courseNameListData]);

  useEffect(() => {
    getActiveCourseNameList(dispatch);
    getTrainingCenterList(dispatch);
    getInstructorList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (decodedId) {
      fetchBatchById();
    }
  }, [decodedId]);

  useEffect(() => {
    return setupQuillCharacterLimit(descriptionQuillRef);
  }, []);

  useEffect(() => {
    if (isOfflineCourse) {
      return setupQuillCharacterLimit(scheduleInfoQuillRef);
    }
  }, [isOfflineCourse]);

  const fetchBatchById = async () => {
    try {
      setShowAlternative({
        loader: true,
        error: "",
      });
      const response = await getBatchById(decodedId);

      if (response?.code === 200) {
        const data = response.data;

        setFormData({
          ...initialFields,

          courseID: {
            value: data?.courses?.ID || "",
            error: "",
          },

          name: {
            value: data?.name || "",
            error: "",
            isChanged: false,
          },

          registrationStartDate: {
            value: data?.registrationStartDate
              ? format(new Date(data.registrationStartDate), "yyyy-MM-dd")
              : "",
            error: "",
            isChanged: false,
          },

          registrationEndDate: {
            value: data?.registrationEndDate
              ? format(new Date(data.registrationEndDate), "yyyy-MM-dd")
              : "",
            error: "",
            isChanged: false,
          },

          batchStartDate: {
            value: data?.batchStartDate
              ? format(new Date(data.batchStartDate), "yyyy-MM-dd")
              : "",
            error: "",
            isChanged: false,
          },

          batchEndDate: {
            value: data?.batchEndDate
              ? format(new Date(data.batchEndDate), "yyyy-MM-dd")
              : "",
            error: "",
            isChanged: false,
          },

          batchStartTime: {
            value: data?.startTime ? formatTime24Hour(data.startTime) : "",
            error: "",
            isChanged: false,
          },

          batchEndTime: {
            value: data?.endTime ? formatTime24Hour(data.endTime) : "",
            error: "",
            isChanged: false,
          },

          trainerIds: {
            value: String(data?.trainers?.length || 0),
            error: "",
            isChanged: false,
          },

          batchLanguage: {
            value: data?.batchLanguage || "",
            error: "",
            isChanged: false,
          },

          Trainers: {
            value: data?.trainers?.map((trainer) => trainer.ID) || [],
            error: "",
            isChanged: false,
          },

          description: {
            value: data?.description || "",
            error: "",
            isChanged: false,
          },

          scheduleInfo: {
            value: data?.scheduleInfo || "",
            error: "",
            isChanged: false,
          },

          trainingCenterID: {
            value: data?.trainingCenters?.ID || "",
            error: "",
            isChanged: false,
          },

          minParticipant: {
            value: String(data?.minParticipants || ""),
            error: "",
            isChanged: false,
          },

          maxParticipant: {
            value: String(data?.maxParticipants || ""),
            error: "",
            isChanged: false,
          },

          virtualMeetingUrl: {
            value: data?.virtualMeetingUrl || "",
            error: "",
            isChanged: false,
          },

          capacityStatus: {
            value: data?.capacityStatus || "",
            error: "",
            isChanged: false,
          },

          status: {
            value: data?.status || "ACTIVE",
            error: "",
            isChanged: false,
          },
        });
      } else {
        const message = Array.isArray(response?.message)
          ? response.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : response?.message || "Failed to fetch batch.";

        notify("danger", message);
      }
    } catch (error) {
      setShowAlternative({
        loader: false,
        error: error?.message || "Something went wrong while fetching batch",
      });
    } finally {
      setShowAlternative({
        loader: false,
        error: "",
      });
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed, formData);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  // Get difference of days between registration and batch dates
  const getDaysDifference = (start, end) => {
    if (!start || !end) return null;

    const startDate = new Date(start);
    const endDate = new Date(end);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  // Get difference of minutes batch start and end time
  const getMinutesDifference = (startTime, endTime) => {
    if (!startTime || !endTime) return null;

    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);

    return eh * 60 + em - (sh * 60 + sm);
  };

  const getValidationError = (field, value, formData) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    const getQuillTextLength = (value, fieldName) => {
      const ref =
        fieldName === "description"
          ? descriptionQuillRef
          : fieldName === "scheduleInfo"
            ? scheduleInfoQuillRef
            : null;
      if (ref && ref.current) {
        return ref.current.getEditor().getLength() - 1;
      }
      const div = document.createElement("div");
      div.innerHTML = value || "";
      return div.textContent.length;
    };

    switch (field) {
      case "courseID":
        return !trimmedValue ? "Please select course name" : "";

      case "name":
        return !trimmedValue ? "Please enter batch name" : "";

      case "virtualMeetingUrl": {
        const shouldShowVirtualMeetingUrl =
          selectedCourse?.courseType === "LIVE" &&
          ["ONLINE", "BOTH"].includes(selectedCourse?.contentDeliveryMode);

        if (!shouldShowVirtualMeetingUrl) {
          return "";
        }

        const url = trimmedValue?.trim();

        if (!url) {
          return "Please enter live session link";
        }

        if (!REGEX.MS_TEAMS_LINK_REGEX.test(url)) {
          return "Please enter a valid microsoft teams link";
        }

        return "";
      }

      case "registrationStartDate":
        if (!trimmedValue) {
          return "Please select registration start date";
        }

        if (
          formData.registrationEndDate.value &&
          new Date(trimmedValue) > new Date(formData.registrationEndDate.value)
        ) {
          return "Registration start date cannot be after registration end date.";
        }

        if (
          formData.batchStartDate.value &&
          getDaysDifference(trimmedValue, formData.batchStartDate.value) < 1
        ) {
          return "Registration start date must be at least 1 day before batch start date.";
        }

        return "";

      case "registrationEndDate":
        if (!trimmedValue) {
          return "Please select registration end date";
        }

        if (
          formData.registrationStartDate.value &&
          new Date(trimmedValue) <
            new Date(formData.registrationStartDate.value)
        ) {
          return "Registration end date cannot be before registration start date.";
        }

        return "";

      case "batchStartDate":
        if (!trimmedValue) {
          return "Please select batch start date";
        }

        if (
          formData.registrationStartDate.value &&
          getDaysDifference(
            formData.registrationStartDate.value,
            trimmedValue,
          ) < 1
        ) {
          return "Batch start date must be at least 1 day after registration start date.";
        }

        if (
          formData.batchEndDate.value &&
          new Date(trimmedValue) > new Date(formData.batchEndDate.value)
        ) {
          return "Batch start date cannot be after batch end date.";
        }

        return "";

      case "batchEndDate":
        if (!trimmedValue) {
          return "Please select batch end date";
        }

        if (
          formData.batchStartDate.value &&
          new Date(trimmedValue) < new Date(formData.batchStartDate.value)
        ) {
          return "Batch end date cannot be before batch start date.";
        }

        return "";

      case "batchStartTime":
        if (!trimmedValue) {
          return "Please select start time";
        }

        if (formData.batchEndTime.value) {
          const diff = getMinutesDifference(
            trimmedValue,
            formData.batchEndTime.value,
          );

          if (diff <= 0) {
            return "Start time must be before end time.";
          }

          if (diff < 30) {
            return "Start time must be at least 30 minutes before end time.";
          }
        }

        return "";

      case "batchEndTime":
        if (!trimmedValue) {
          return "Please select end time";
        }

        if (formData.batchStartTime.value) {
          const diff = getMinutesDifference(
            formData.batchStartTime.value,
            trimmedValue,
          );

          if (diff <= 0) {
            return "End time must be after start time.";
          }

          if (diff < 30) {
            return "End time must be at least 30 minutes after start time.";
          }
        }

        return "";

      case "trainingCenterID":
        if (
          selectedCourse?.courseType === "LIVE" &&
          ["OFFLINE", "BOTH"].includes(selectedCourse?.contentDeliveryMode)
        ) {
          return !trimmedValue ? "Please select offline training center" : "";
        }

        return "";

      case "batchLanguage":
        return !value ? "Please select batch language" : "";

      case "capacityStatus":
        return mode === "edit" && !value ? "Please select capacity status" : "";

      case "trainerIds":
        if (!trimmedValue) return "Please enter number of instructors";
        if (!REGEX.ONLY_DIGITS_REGEX.test(trimmedValue)) return "Enter a valid number";
        if (Number(trimmedValue) <= 0) {
          return "Number of instructors must be greater than 0";
        }
        return "";

      case "Trainers":
        if (!value || value.length === 0) {
          return "Please select at least one instructor";
        }
        const noOfTrainers = parseInt(formData.trainerIds.value, 10);
        if (value.length !== noOfTrainers) {
          return `Please select exactly ${noOfTrainers} instructors`;
        }
        return "";

      case "minParticipant":
        if (!trimmedValue) return "";

        if (Number(trimmedValue) < 1) {
          return "Minimum participants must be at least 1";
        }

        if (
          formData.maxParticipant?.value &&
          Number(trimmedValue) >= Number(formData.maxParticipant.value)
        ) {
          return "Minimum participants must be less than maximum participants";
        }

        return "";

      case "maxParticipant":
        if (!trimmedValue) return "";

        if (Number(trimmedValue) < 1) {
          return "Maximum participants must be at least 1";
        }

        if (
          formData.minParticipant?.value &&
          Number(trimmedValue) <= Number(formData.minParticipant.value)
        ) {
          return "Maximum participants must be greater than minimum participants";
        }

        return "";

      case "description": {
        const textLength = getQuillTextLength(value, "description");

        if (!textLength) {
          return "Please enter batch description";
        }

        if (textLength > MAX_DESCRIPTION_LENGTH) {
          return `Batch description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`;
        }

        return "";
      }

      case "scheduleInfo": {
        // Schedule information is required only for offline courses
        if (!isOfflineCourse) {
          return "";
        }

        const textLength = getQuillTextLength(value, "scheduleInfo");

        if (!textLength) {
          return "Please enter batch schedule description";
        }

        if (textLength > MAX_DESCRIPTION_LENGTH) {
          return `Batch schedule description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`;
        }

        return "";
      }

      case "status":
        return !value ? "Please select status" : "";

      default:
        return "";
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      if (
        (field === "minParticipant" ||
          field === "maxParticipant" ||
          field === "trainerIds") &&
        value !== "" &&
        !REGEX.ONLY_DIGITS_REGEX.test(value)
      ) {
        return prev;
      }

      let updated = {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: "",
          isChanged: true,
        },
      };

      if (field === "minParticipant") {
        updated.maxParticipant = {
          ...updated.maxParticipant,
          error: "",
        };
      }

      if (field === "maxParticipant") {
        updated.minParticipant = {
          ...updated.minParticipant,
          error: "",
        };
      }

      if (field === "batchStartTime" && updated.batchEndTime) {
        updated.batchEndTime.error = "";
      }

      if (field === "batchEndTime" && updated.batchStartTime) {
        updated.batchStartTime.error = "";
      }

      // Validate selected trainers when number of trainers changes
      if (field === "trainerIds") {
        const noOfTrainers = parseInt(value, 10);
        const selectedTrainers = prev.Trainers.value || [];

        let trainerError = "";

        if (value && REGEX.ONLY_DIGITS_REGEX.test(value) && noOfTrainers > 0) {
          if (selectedTrainers.length !== noOfTrainers) {
            trainerError = `Please select exactly ${noOfTrainers} instructors`;
          }
        }

        updated.Trainers = {
          ...prev.Trainers,
          error: trainerError,
          isChanged: true,
        };
      }

      return updated;
    });
  };

  const courseOptions =
    courseNameListData?.data?.map((course) => ({
      value: course.ID,
      label: course.courseName,
    })) || [];

  const instructorOptions =
    instructorListData?.data?.trainers
      ?.filter((trainer) => trainer.user?.status === "ACTIVE")
      .map((trainer) => ({
        value: trainer.ID,
        label: trainer.user?.fullName,
      })) || [];

  const trainingCenterOptions =
    trainingCentersDataList?.data?.trainingCenters
      ?.filter((center) => center.status === "ACTIVE")
      .map((center) => ({
        value: center.ID,
        label: center.name,
      })) || [];

  const validateForm = () => {
    let newBatch = { ...formData };

    let hasError = false;

    const sectionsToOpen = {
      batchInfo: false,
      capacityEnrollment: false,
      registrationTimeline: false,
      batchSchedule: false,
      description: false,
      scheduleInfo: false,
    };

    const errorSections = new Set();

    Object.keys(newBatch).forEach((key) => {
      newBatch[key] = {
        ...newBatch[key],
        error: getValidationError(key, newBatch[key].value, newBatch),
      };
    });

    for (const key in newBatch) {
      if (newBatch[key].error) {
        hasError = true;

        const section = batchFieldToSectionMap[key];

        if (section) {
          sectionsToOpen[section] = true;
          errorSections.add(batchSectionLabels[section]);
        }
      }
    }

    if (hasError) {
      setOpen((prev) => ({
        ...prev,
        ...sectionsToOpen,
      }));
    }

    setFormData(newBatch);

    return {
      isValid: !hasError,
      errorSections: [...errorSections],
    };
  };

  const formatSections = (sections) => {
    if (sections.length === 1) return sections[0];
    if (sections.length === 2) return sections.join(" and ");

    return `${sections.slice(0, -1).join(", ")} and ${sections.at(-1)}`;
  };

  const handleSubmit = async () => {
    const { isValid, errorSections } = validateForm();

    if (!isValid) {
      notify(
        "danger",
        `Please complete the required fields in ${formatSections(
          errorSections,
        )} before saving.`,
      );
      return;
    }

    const shouldShowTrainingCenter =
      selectedCourse?.courseType === "LIVE" &&
      ["OFFLINE", "BOTH"].includes(selectedCourse?.contentDeliveryMode);

    try {
      setIsSaving(true);
      let response;

      if (decodedId) {
        // Update: send only changed fields
        let payload = {};

        Object.entries(formData).forEach(([key, field]) => {
          if (field.isChanged) {
            payload[key] = field.value;
          }
        });

        if (payload.Trainers) {
          payload.trainerIds = payload.Trainers;
          delete payload.Trainers;
        }

        if (payload.batchStartTime) {
          payload.startTime = payload.batchStartTime;
          delete payload.batchStartTime;
        }

        if (payload.batchEndTime) {
          payload.endTime = payload.batchEndTime;
          delete payload.batchEndTime;
        }

        if (payload.minParticipant !== undefined) {
          payload.minParticipants = Number(payload.minParticipant);
          delete payload.minParticipant;
        }

        if (payload.maxParticipant !== undefined) {
          payload.maxParticipants = Number(payload.maxParticipant);
          delete payload.maxParticipant;
        }

        if (!shouldShowTrainingCenter) {
          delete payload.trainingCenterID;
        }

        if (selectedCourse?.contentDeliveryMode === "OFFLINE") {
          delete payload.virtualMeetingUrl;
        }

        response = await updateBatchById(decodedId, payload);
      } else {
        // Add: send complete payload
        const payload = {
          courseID: formData.courseID.value,
          name: formData.name.value,

          ...(shouldShowTrainingCenter && {
            trainingCenterID: Number(formData.trainingCenterID.value),
          }),

          trainerIds: formData.Trainers.value,
          ...(selectedCourse?.contentDeliveryMode !== "OFFLINE" && {
            virtualMeetingUrl: formData.virtualMeetingUrl.value,
          }),
          registrationStartDate: formData.registrationStartDate.value,
          registrationEndDate: formData.registrationEndDate.value,
          batchStartDate: formData.batchStartDate.value,
          batchLanguage: formData.batchLanguage.value,
          batchEndDate: formData.batchEndDate.value,
          startTime: formData.batchStartTime.value,
          endTime: formData.batchEndTime.value,
          minParticipants: Number(formData.minParticipant.value) || 0,
          maxParticipants: Number(formData.maxParticipant.value) || 0,
          description: formData.description.value,
          ...(isOfflineCourse && {
            scheduleInfo: formData.scheduleInfo.value,
          }),
          status: formData.status.value,
          cancellationRemark: "",
        };

        response = await addBatch(payload);
      }

      if (response?.code === 200) {
        notify(
          "success",
          response?.message ||
            `Batch ${decodedId ? "updated" : "created"} successfully.`,
        );
        if (location.state?.fromCourseSchedule) {
          navigate(-2);
        } else {
          navigate(
            role === "SUPER_ADMIN" ? "/super-admin/batch" : "/admin/batch",
          );
        }
      } else {
        const message = Array.isArray(response?.message)
          ? response.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : response?.message || "Failed to save batch.";

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.message || "Failed to save batch. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const setupQuillCharacterLimit = (quillRef) => {
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();

    const handleKeyDown = (e) => {
      if (
        e.ctrlKey ||
        e.metaKey ||
        e.altKey ||
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key.startsWith("Arrow") ||
        e.key === "Tab"
      ) {
        return;
      }

      const textLength = editor.getLength() - 1;

      if (textLength >= MAX_DESCRIPTION_LENGTH) {
        e.preventDefault();
      }
    };

    const handlePaste = (e) => {
      const textLength = editor.getLength() - 1;

      const pastedText =
        e.clipboardData?.getData("text/plain") ||
        e.clipboardData?.getData("text") ||
        "";

      const selection = editor.getSelection() || {
        index: textLength,
        length: 0,
      };

      const remaining =
        MAX_DESCRIPTION_LENGTH - (textLength - selection.length);

      if (remaining <= 0) {
        e.preventDefault();
        return;
      }

      if (pastedText.length > remaining) {
        e.preventDefault();

        editor.deleteText(selection.index, selection.length);

        editor.insertText(selection.index, pastedText.substring(0, remaining));
      }
    };

    editor.root.addEventListener("keydown", handleKeyDown);
    editor.root.addEventListener("paste", handlePaste);

    return () => {
      editor.root.removeEventListener("keydown", handleKeyDown);
      editor.root.removeEventListener("paste", handlePaste);
    };
  };

  return (
    <>
      <AdminHeader parentName="Batch" />
      <Container fluid className="mt--6">
        <Row className="justify-content-center">
          <Col>
            <div
              className="d-flex align-items-center cursor text-white"
              onClick={
                () => navigate(-1)
                // navigate(
                //   role === "SUPER_ADMIN"
                //     ? "/super-admin/batch"
                //     : "/admin/batch",
                // )
              }
            >
              <i className="fa-solid fa-angle-left mr-2" />
              <span className="font-weight-bold">
                {mode === "edit" ? "Edit Batch" : "Add Batch"}
              </span>
            </div>

            {mode === "edit" && showAlternative.loader ? (
              <Card className="mt-3 details-state-card">
                <CardBody className="details-state-body">
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                </CardBody>
              </Card>
            ) : showAlternative.error ? (
              <Card className="mt-3 details-state-card">
                <CardBody className="details-state-body">
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i class="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{showAlternative.error}</span>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <>
                <Card className="mb-3 mt-3">
                  <CardHeader>
                    <SectionHeader
                      title="Batch Information & Capacity"
                      isOpen={open.batchInfo}
                      onClick={() => toggle("batchInfo")}
                    />
                  </CardHeader>

                  <Collapse isOpen={open.batchInfo}>
                    <CardBody>
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Course Name{" "}
                              <span className="text-danger"> *</span>
                            </Label>

                            <Select
                              name="courseID"
                              options={courseOptions}
                              placeholder="Select course name"
                              styles={SELECT_CUSTOM_STYLES}
                              classNamePrefix="react-select"
                              isDisabled={courseListID > 0 || mode === "edit"}
                              value={
                                courseOptions.find(
                                  (option) =>
                                    option.value === formData.courseID.value,
                                ) || null
                              }
                              onChange={(selected) => {
                                const courseId = selected
                                  ? Number(selected.value)
                                  : null;

                                const course = courseNameListData?.data?.find(
                                  (item) => item.ID === courseId,
                                );
                                handleChange("courseID", courseId);

                                if (course?.courseLanguage === "ENGLISH") {
                                  handleChange("batchLanguage", "ENGLISH");
                                } else if (course?.courseLanguage === "HINDI") {
                                  handleChange("batchLanguage", "HINDI");
                                } else {
                                  handleChange("batchLanguage", "");
                                }
                              }}
                              onBlur={() =>
                                handleOnBlur(
                                  "courseID",
                                  formData.courseID.value,
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="absolute"
                              className={
                                formData.courseID.error ? "is-invalid" : ""
                              }
                            />

                            {formData.courseID.error && mode !== "edit" && (
                              <div className="invalid-feedback d-block">
                                {formData.courseID.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Batch Name <span className="text-danger"> *</span>
                            </Label>
                            <Input
                              type="text"
                              name="name"
                              value={formData.name.value}
                              onChange={(e) =>
                                handleChange("name", e.target.value)
                              }
                              maxLength={100}
                              placeholder="Enter batch name"
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                formData.name.error ? "is-invalid" : ""
                              }
                            />
                            {formData.name.error && (
                              <small className="invalid-feedback d-block">
                                {formData.name.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Batch Language
                            </Label>
                            <span className="text-danger"> *</span>

                            <Select
                              options={COURSE_LANGUAGES}
                              placeholder="Select batch language"
                              styles={SELECT_CUSTOM_STYLES}
                              classNamePrefix="react-select"
                              value={
                                COURSE_LANGUAGES.find(
                                  (option) =>
                                    option.value ===
                                    formData.batchLanguage.value,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleChange(
                                  "batchLanguage",
                                  selected ? selected.value : "",
                                )
                              }
                              onBlur={() =>
                                handleOnBlur(
                                  "batchLanguage",
                                  formData.batchLanguage.value,
                                )
                              }
                              isDisabled={
                                selectedCourseLanguage &&
                                selectedCourseLanguage !== "ENGLISH_HINDI" &&
                                selectedCourseLanguage !== "BOTH"
                              }
                              menuPortalTarget={document.body}
                              menuPosition="absolute"
                              className={
                                formData.batchLanguage.error ? "is-invalid" : ""
                              }
                            />

                            {formData.batchLanguage.error && (
                              <div className="invalid-feedback d-block">
                                {formData.batchLanguage.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        {selectedCourse?.courseType === "LIVE" &&
                          ["OFFLINE", "BOTH"].includes(
                            selectedCourse?.contentDeliveryMode,
                          ) && (
                            <Col md="4">
                              <FormGroup>
                                <Label className="form-control-label">
                                  Offline Training Center
                                  <span className="text-danger">*</span>
                                </Label>

                                <Select
                                  options={trainingCenterOptions}
                                  placeholder="Select offline training center"
                                  styles={SELECT_CUSTOM_STYLES}
                                  classNamePrefix="react-select"
                                  value={
                                    trainingCenterOptions.find(
                                      (option) =>
                                        option.value ===
                                        formData.trainingCenterID.value,
                                    ) || null
                                  }
                                  onChange={(selected) =>
                                    handleChange(
                                      "trainingCenterID",
                                      selected ? Number(selected.value) : null,
                                    )
                                  }
                                  onBlur={() =>
                                    handleOnBlur(
                                      "trainingCenterID",
                                      formData.trainingCenterID.value,
                                    )
                                  }
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  className={
                                    formData.trainingCenterID.error
                                      ? "is-invalid"
                                      : ""
                                  }
                                />

                                {formData.trainingCenterID.error && (
                                  <div className="invalid-feedback d-block">
                                    {formData.trainingCenterID.error}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                          )}

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Number Of Instructors
                              <span className="text-danger"> *</span>
                            </Label>
                            <Input
                              type="tel"
                              inputMode="numeric"
                              maxLength={2}
                              placeholder="Enter number of instructors"
                              value={formData.trainerIds.value}
                              onChange={(e) =>
                                handleChange("trainerIds", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur("trainerIds", e.target.value)
                              }
                              className={
                                formData.trainerIds.error ? "is-invalid" : ""
                              }
                            />
                            {formData.trainerIds.error && (
                              <small className="invalid-feedback d-block">
                                {formData.trainerIds.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Instructors
                            </Label>
                            <span className="text-danger"> *</span>
                            <Select
                              isMulti
                              name="Trainers"
                              options={instructorOptions}
                              placeholder="Select instructor"
                              styles={SELECT_CUSTOM_STYLES}
                              classNamePrefix="react-select"
                              value={instructorOptions.filter((option) =>
                                formData.Trainers.value.includes(option.value),
                              )}
                              onChange={(selected) =>
                                handleChange(
                                  "Trainers",
                                  selected
                                    ? selected.map((item) => Number(item.value))
                                    : [],
                                )
                              }
                              isDisabled={
                                !formData.trainerIds.value ||
                                formData.trainerIds.value <= 0
                              }
                              onBlur={() =>
                                handleOnBlur(
                                  "Trainers",
                                  formData.Trainers.value,
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="absolute"
                              className={
                                formData.Trainers.error ? "is-invalid" : ""
                              }
                              isOptionDisabled={() =>
                                formData.Trainers.value.length >=
                                parseInt(formData.trainerIds.value || 0, 10)
                              }
                            />
                            {formData.Trainers.error && (
                              <div className="invalid-feedback d-block">
                                {formData.Trainers.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        {selectedCourse?.courseType === "LIVE" &&
                          ["ONLINE", "BOTH"].includes(
                            selectedCourse?.contentDeliveryMode,
                          ) && (
                            <Col md="4">
                              <FormGroup>
                                <Label className="form-control-label">
                                  Live session link
                                  <span className="text-danger"> *</span>
                                </Label>

                                <Input
                                  type="text"
                                  name="virtualMeetingUrl"
                                  value={formData.virtualMeetingUrl.value}
                                  onChange={(e) =>
                                    handleChange(
                                      "virtualMeetingUrl",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Enter live session link"
                                  onBlur={(e) =>
                                    handleOnBlur(e.target.name, e.target.value)
                                  }
                                  maxLength={150}
                                  className={
                                    formData.virtualMeetingUrl.error
                                      ? "is-invalid"
                                      : ""
                                  }
                                />

                                {formData.virtualMeetingUrl.error && (
                                  <small className="invalid-feedback d-block">
                                    {formData.virtualMeetingUrl.error}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>
                          )}
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label mt-1">
                              Min Participants
                              <InfoPopover
                                targetId="minParticipantInfo"
                                title="Min Participants"
                                content="Sets the minimum number of learners who can enroll."
                              />
                            </Label>
                            {/* <div className="mt-2"> */}
                            <Input
                              type="tel"
                              name="minParticipant"
                              inputMode="numeric"
                              maxLength={3}
                              placeholder="Enter min participants"
                              value={formData.minParticipant.value}
                              onChange={(e) =>
                                handleChange("minParticipant", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur("minParticipant", e.target.value)
                              }
                              className={
                                formData.minParticipant.error
                                  ? "is-invalid"
                                  : ""
                              }
                            />
                            {formData.minParticipant.error && (
                              <small className="invalid-feedback d-block">
                                {formData.minParticipant.error}
                              </small>
                            )}
                            {/* </div> */}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label mt-1">
                              Max Participants
                              <InfoPopover
                                targetId="maxParticipantsInfo"
                                title="Max Participants"
                                content="Sets the maximum number of learners who can enroll."
                              />
                            </Label>
                            {/* <div className="mt-2"> */}
                            <Input
                              type="tel"
                              name="maxParticipant"
                              inputMode="numeric"
                              maxLength={3}
                              placeholder="Enter max participants"
                              value={formData.maxParticipant.value}
                              onChange={(e) =>
                                handleChange("maxParticipant", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur("maxParticipant", e.target.value)
                              }
                              className={
                                formData.maxParticipant.error
                                  ? "is-invalid"
                                  : ""
                              }
                            />
                            {formData.maxParticipant.error && (
                              <small className="invalid-feedback d-block">
                                {formData.maxParticipant.error}
                              </small>
                            )}
                            {/* </div> */}
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        {mode === "edit" && (
                          <Col md="4">
                            <FormGroup>
                              <Label className="form-control-label">
                                Capacity Status
                                <span className="text-danger"> *</span>
                              </Label>

                              <div className="d-flex mt-2">
                                <FormGroup check className="mr-4">
                                  <Label check>
                                    <Input
                                      type="radio"
                                      name="capacityStatus"
                                      className="cursor"
                                      checked={
                                        formData.capacityStatus.value === "OPEN"
                                      }
                                      onChange={() =>
                                        handleChange("capacityStatus", "OPEN")
                                      }
                                    />
                                    Open
                                  </Label>
                                </FormGroup>

                                <FormGroup check>
                                  <Label check>
                                    <Input
                                      type="radio"
                                      name="capacityStatus"
                                      className="cursor"
                                      checked={
                                        formData.capacityStatus.value === "FULL"
                                      }
                                      onChange={() =>
                                        handleChange("capacityStatus", "FULL")
                                      }
                                    />
                                    Full
                                  </Label>
                                </FormGroup>
                              </div>
                              {formData.capacityStatus.error && (
                                <div className="invalid-feedback d-block">
                                  {formData.capacityStatus.error}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Collapse>
                </Card>
                {/* <Card className="mb-3">
                  <CardHeader>
                    <SectionHeader
                      title="Capacity & Enrollment"
                      isOpen={open.capacityEnrollment}
                      onClick={() => toggle("capacityEnrollment")}
                    />
                  </CardHeader>

                  <Collapse isOpen={open.capacityEnrollment}>
                    <CardBody>
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label mt-1">
                              Min Participants
                              <InfoPopover
                                targetId="minParticipantInfo"
                                title="Min Participants"
                                content="Sets the minimum number of learners who can enroll."
                              />
                            </Label>
                            <Input
                              type="tel"
                              name="minParticipant"
                              inputMode="numeric"
                              maxLength={3}
                              placeholder="Enter min participants"
                              value={formData.minParticipant.value}
                              onChange={(e) =>
                                handleChange("minParticipant", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur("minParticipant", e.target.value)
                              }
                              className={
                                formData.minParticipant.error
                                  ? "is-invalid"
                                  : ""
                              }
                            />
                            {formData.minParticipant.error && (
                              <small className="invalid-feedback d-block">
                                {formData.minParticipant.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label mt-1">
                              Max Participants
                              <InfoPopover
                                targetId="maxParticipantsInfo"
                                title="Max Participants"
                                content="Sets the maximum number of learners who can enroll."
                              />
                            </Label>
                            <Input
                              type="tel"
                              name="maxParticipant"
                              inputMode="numeric"
                              maxLength={3}
                              placeholder="Enter max participants"
                              value={formData.maxParticipant.value}
                              onChange={(e) =>
                                handleChange("maxParticipant", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur("maxParticipant", e.target.value)
                              }
                              className={
                                formData.maxParticipant.error
                                  ? "is-invalid"
                                  : ""
                              }
                            />
                            {formData.maxParticipant.error && (
                              <small className="invalid-feedback d-block">
                                {formData.maxParticipant.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        {mode === "edit" && (
                          <Col md="4">
                            <FormGroup>
                              <Label className="form-control-label">
                                Capacity Status
                                <span className="text-danger">*</span>
                              </Label>

                              <div className="d-flex mt-2">
                                <FormGroup check className="mr-4">
                                  <Label check>
                                    <Input
                                      type="radio"
                                      name="capacityStatus"
                                      className="cursor"
                                      checked={
                                        formData.capacityStatus.value === "OPEN"
                                      }
                                      onChange={() =>
                                        handleChange("capacityStatus", "OPEN")
                                      }
                                    />
                                    Open
                                  </Label>
                                </FormGroup>

                                <FormGroup check>
                                  <Label check>
                                    <Input
                                      type="radio"
                                      name="capacityStatus"
                                      className="cursor"
                                      checked={
                                        formData.capacityStatus.value === "FULL"
                                      }
                                      onChange={() =>
                                        handleChange("capacityStatus", "FULL")
                                      }
                                    />
                                    Full
                                  </Label>
                                </FormGroup>
                              </div>
                              {formData.capacityStatus.error && (
                                <div className="invalid-feedback d-block">
                                  {formData.capacityStatus.error}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Collapse>
                </Card> */}
                <Card className="mb-3">
                  <CardHeader>
                    <SectionHeader
                      title="Schedule & Registration"
                      isOpen={open.registrationTimeline}
                      onClick={() => toggle("registrationTimeline")}
                    />
                  </CardHeader>

                  <Collapse isOpen={open.registrationTimeline}>
                    <CardBody>
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Registration Start Date
                              <span className="text-danger"> *</span>
                            </Label>
                            <DatePicker
                              selected={
                                formData.registrationStartDate.value
                                  ? new Date(
                                      formData.registrationStartDate.value,
                                    )
                                  : null
                              }
                              onChange={(date) =>
                                handleChange(
                                  "registrationStartDate",
                                  date ? format(date, "yyyy-MM-dd") : "",
                                )
                              }
                              onBlur={() =>
                                handleOnBlur(
                                  "registrationStartDate",
                                  formData.registrationStartDate.value,
                                )
                              }
                              dateFormat="dd-MMM-yyyy"
                              placeholderText="Select registration start date"
                              minDate={new Date()}
                              showIcon
                              showMonthDropdown
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={100}
                              icon={
                                <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                              }
                              toggleCalendarOnIconClick
                              className={`form-control ${
                                formData.registrationStartDate.error
                                  ? "is-invalid"
                                  : ""
                              }`}
                              wrapperClassName="w-100"
                              calendarClassName="custom-calendar"
                              popperPlacement="bottom-start"
                              onKeyDown={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                            />
                            {formData.registrationStartDate.error && (
                              <small className="invalid-feedback d-block">
                                {formData.registrationStartDate.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Registration End Date
                              <span className="text-danger"> *</span>
                            </Label>
                            <DatePicker
                              selected={
                                formData.registrationEndDate.value
                                  ? new Date(formData.registrationEndDate.value)
                                  : null
                              }
                              onChange={(date) =>
                                handleChange(
                                  "registrationEndDate",
                                  date ? format(date, "yyyy-MM-dd") : "",
                                )
                              }
                              onBlur={() =>
                                handleOnBlur(
                                  "registrationEndDate",
                                  formData.registrationEndDate.value,
                                )
                              }
                              dateFormat="dd-MMM-yyyy"
                              placeholderText="Select registration end date"
                              minDate={
                                formData.registrationStartDate.value
                                  ? new Date(
                                      formData.registrationStartDate.value,
                                    )
                                  : null
                              }
                              showIcon
                              showMonthDropdown
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={100}
                              icon={
                                <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                              }
                              toggleCalendarOnIconClick
                              className={`form-control ${
                                formData.registrationEndDate.error
                                  ? "is-invalid"
                                  : ""
                              }`}
                              wrapperClassName="w-100"
                              calendarClassName="custom-calendar"
                              popperPlacement="bottom-start"
                              onKeyDown={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                              style={styles.datePicker}
                            />
                            {formData.registrationEndDate.error && (
                              <small className="invalid-feedback d-block">
                                {formData.registrationEndDate.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Batch Start Date
                              <span className="text-danger"> *</span>
                            </Label>
                            <DatePicker
                              selected={
                                formData.batchStartDate.value
                                  ? new Date(formData.batchStartDate.value)
                                  : null
                              }
                              onChange={(date) =>
                                handleChange(
                                  "batchStartDate",
                                  date ? format(date, "yyyy-MM-dd") : "",
                                )
                              }
                              onBlur={() =>
                                handleOnBlur(
                                  "batchStartDate",
                                  formData.batchStartDate.value,
                                )
                              }
                              dateFormat="dd-MMM-yyyy"
                              placeholderText="Select batch start date"
                              minDate={new Date()}
                              showIcon
                              showMonthDropdown
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={100}
                              icon={
                                <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                              }
                              toggleCalendarOnIconClick
                              className={`form-control ${
                                formData.batchStartDate.error
                                  ? "is-invalid"
                                  : ""
                              }`}
                              wrapperClassName="w-100"
                              calendarClassName="custom-calendar"
                              popperPlacement="bottom-start"
                              onKeyDown={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                            />
                            {formData.batchStartDate.error && (
                              <small className="invalid-feedback d-block">
                                {formData.batchStartDate.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Batch End Date
                              <span className="text-danger"> *</span>
                            </Label>
                            <DatePicker
                              selected={
                                formData.batchEndDate.value
                                  ? new Date(formData.batchEndDate.value)
                                  : null
                              }
                              onChange={(date) =>
                                handleChange(
                                  "batchEndDate",
                                  date ? format(date, "yyyy-MM-dd") : "",
                                )
                              }
                              onBlur={() =>
                                handleOnBlur(
                                  "batchEndDate",
                                  formData.batchEndDate.value,
                                )
                              }
                              dateFormat="dd-MMM-yyyy"
                              placeholderText="Select batch end date"
                              minDate={formData.batchStartDate.value}
                              showIcon
                              showMonthDropdown
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={100}
                              icon={
                                <i className="fa-solid fa-calendar-day fa-md mt-1 mr-4 text-muted" />
                              }
                              toggleCalendarOnIconClick
                              className={`form-control ${
                                formData.batchEndDate.error ? "is-invalid" : ""
                              }`}
                              wrapperClassName="w-100"
                              calendarClassName="custom-calendar"
                              popperPlacement="bottom-start"
                              onKeyDown={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                            />
                            {formData.batchEndDate.error && (
                              <small className="invalid-feedback d-block">
                                {formData.batchEndDate.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="2">
                          <FormGroup>
                            <Label className="form-control-label">
                              Start Time<span className="text-danger"> *</span>
                            </Label>
                            <CustomTimePicker
                              value={parseTimeStringToDate(
                                formData.batchStartTime.value,
                              )}
                              onChange={(date) =>
                                handleChange(
                                  "batchStartTime",
                                  date ? format(date, "HH:mm") : "",
                                )
                              }
                              className={`form-control ${
                                formData.batchStartTime.error
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Select start time"
                              onBlur={() =>
                                handleOnBlur(
                                  "batchStartTime",
                                  formData.batchStartTime.value,
                                )
                              }
                            />
                            {formData.batchStartTime.error && (
                              <small className="invalid-feedback d-block">
                                {formData.batchStartTime.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="2">
                          <FormGroup>
                            <Label className="form-control-label">
                              End Time<span className="text-danger"> *</span>
                            </Label>
                            <CustomTimePicker
                              value={parseTimeStringToDate(
                                formData.batchEndTime.value,
                              )}
                              onChange={(date) =>
                                handleChange(
                                  "batchEndTime",
                                  date ? format(date, "HH:mm") : "",
                                )
                              }
                              minTime={
                                formData.batchStartTime.value
                                  ? new Date(
                                      parseTimeStringToDate(
                                        formData.batchStartTime.value,
                                      ).getTime() +
                                        30 * 60 * 1000,
                                    )
                                  : null
                              }
                              maxTime={parseTimeStringToDate("23:59")}
                              className={`form-control ${
                                formData.batchEndTime.error
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Select end time"
                              onBlur={() =>
                                handleOnBlur(
                                  "batchEndTime",
                                  formData.batchEndTime.value,
                                )
                              }
                            />
                            {formData.batchEndTime.error && (
                              <small className="invalid-feedback d-block">
                                {formData.batchEndTime.error}
                              </small>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                      {isOfflineCourse && (
                        <Row>
                          <Col md="8">
                            <FormGroup>
                              <Label className="form-control-label">
                                Schedule Description
                                <span className="text-danger"> *</span>
                                <InfoPopover
                                  targetId="batchScheduleInfo"
                                  title="Schedule Description"
                                  content="The information provided here will be visible to the trainee."
                                />
                              </Label>

                              <div
                                className={`course-description-quill quill-wrapper ${
                                  formData.scheduleInfo.error
                                    ? "quill-invalid"
                                    : ""
                                }`}
                              >
                                <ReactQuill
                                  ref={scheduleInfoQuillRef}
                                  name="scheduleInfo"
                                  theme="snow"
                                  modules={MODULES}
                                  placeholder="Enter batch schedule information"
                                  value={formData.scheduleInfo.value}
                                  onChange={(value) =>
                                    handleChange("scheduleInfo", value)
                                  }
                                  onBlur={() =>
                                    handleOnBlur(
                                      "scheduleInfo",
                                      formData.scheduleInfo.value,
                                    )
                                  }
                                />
                              </div>

                              {(() => {
                                const len = scheduleInfoQuillRef.current
                                  ? scheduleInfoQuillRef.current
                                      .getEditor()
                                      .getLength() - 1
                                  : 0;

                                return (
                                  <div className="d-flex justify-content-between align-items-center mt-1">
                                    <div className="invalid-feedback d-block m-0">
                                      {formData.scheduleInfo.error || ""}
                                    </div>

                                    <div
                                      className={`font-size-sm ${
                                        len > 2000
                                          ? "text-danger"
                                          : "text-muted"
                                      }`}
                                    >
                                      {len}/2000
                                    </div>
                                  </div>
                                );
                              })()}
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                    </CardBody>
                  </Collapse>
                </Card>
                <Card className="mb-3">
                  <CardHeader>
                    <SectionHeader
                      title="Additional Information"
                      isOpen={open.description}
                      onClick={() => toggle("description")}
                    />
                  </CardHeader>

                  <Collapse isOpen={open.description}>
                    <CardBody>
                      <Row>
                        <Col md="8">
                          <FormGroup>
                            <Label className="form-control-label">
                              Batch Description
                              <span className="text-danger"> *</span>
                              <InfoPopover
                                targetId="batchDescriptionInfo"
                                title="Batch Description"
                                content="This information will be displayed to the trainee as part of the batch details."
                              />
                            </Label>

                            <div
                              className={`course-description-quill quill-wrapper ${formData.description.error ? "quill-invalid" : ""}`}
                            >
                              <ReactQuill
                                ref={descriptionQuillRef}
                                name="description"
                                theme="snow"
                                modules={MODULES}
                                placeholder="Enter batch description"
                                value={formData.description.value}
                                onChange={(value) =>
                                  handleChange("description", value)
                                }
                                onBlur={() =>
                                  handleOnBlur(
                                    "description",
                                    formData.description.value,
                                  )
                                }
                              />
                            </div>
                            {(() => {
                              const len = descriptionQuillRef.current
                                ? descriptionQuillRef.current
                                    .getEditor()
                                    .getLength() - 1
                                : 0;

                              return (
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                  <div className="invalid-feedback d-block m-0">
                                    {formData.description.error || ""}
                                  </div>

                                  <div
                                    className={`font-size-sm ${len > 2000 ? "text-danger" : "text-muted"}`}
                                  >
                                    {len}/2000
                                  </div>
                                </div>
                              );
                            })()}
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Status <span className="text-danger"> *</span>
                            </Label>

                            <div className="d-flex mt-2">
                              <FormGroup check className="mr-4">
                                <Label check>
                                  <Input
                                    type="radio"
                                    name="status"
                                    className="cursor"
                                    checked={formData.status.value === "ACTIVE"}
                                    onChange={() =>
                                      handleChange("status", "ACTIVE")
                                    }
                                  />
                                  Active
                                </Label>
                              </FormGroup>

                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="radio"
                                    name="status"
                                    className="cursor"
                                    checked={
                                      formData.status.value === "INACTIVE"
                                    }
                                    onChange={() =>
                                      handleChange("status", "INACTIVE")
                                    }
                                  />
                                  Inactive
                                </Label>
                              </FormGroup>
                            </div>

                            {formData.status.error && (
                              <div className="invalid-feedback d-block">
                                {formData.status.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Collapse>
                </Card>
                <Row className="mt-4 mb-4">
                  <Col className="text-right">
                    <Button
                      color="secondary"
                      className="mr-2 save-btn-sm"
                      onClick={
                        () => navigate(-1)
                        // navigate(
                        //   role === "SUPER_ADMIN"
                        //     ? "/super-admin/batch"
                        //     : "/admin/batch",
                        // )
                      }
                    >
                      Cancel
                    </Button>

                    <Button
                      className="btn-deep-blue save-btn-sm"
                      onClick={handleSubmit}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <i className="fa fa-spinner fa-spin text-white" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AddBatch;

const styles = {
  datePicker: {
    width: "100%",
    display: "flex",
  },
};
